import React, { useState, useEffect } from 'react';
import Layout from '../../components/Layout';
import LoadingSpinner from '../../components/LoadingSpinner';
import {
  CloudArrowDownIcon,
  CloudArrowUpIcon,
  CloudIcon,
  Cog6ToothIcon,
  ClockIcon,
  DocumentArrowDownIcon,
  TrashIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  PlayIcon,
  StopIcon,
  LinkIcon,
  ServerIcon,
  CalendarIcon
} from '@heroicons/react/24/outline';
import { enhancedBackupService } from '../../services/enhancedBackupService';
import { formatDate } from '../../utils/formatters';
import toast from 'react-hot-toast';

const EnhancedBackupManagement = () => {
  const [localBackups, setLocalBackups] = useState([]);
  const [googleDriveBackups, setGoogleDriveBackups] = useState([]);
  const [googleDriveStatus, setGoogleDriveStatus] = useState({});
  const [schedulerStatus, setSchedulerStatus] = useState({});
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [activeTab, setActiveTab] = useState('backups');
  const [restoring, setRestoring] = useState(false);
  const [showRestoreModal, setShowRestoreModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [schedulerSettings, setSchedulerSettings] = useState({
    frequency: 'daily',
    time: '02:00',
    autoUploadToGoogleDrive: false,
    maxLocalBackups: 10,
    maxGoogleDriveBackups: 30
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      
      const [
        localBackupsResult,
        googleDriveStatusResult,
        schedulerStatusResult
      ] = await Promise.allSettled([
        enhancedBackupService.listLocalBackups(),
        enhancedBackupService.getGoogleDriveStatus(),
        enhancedBackupService.getSchedulerStatus()
      ]);

      if (localBackupsResult.status === 'fulfilled' && localBackupsResult.value.success) {
        setLocalBackups(localBackupsResult.value.backups || []);
      }

      if (googleDriveStatusResult.status === 'fulfilled' && googleDriveStatusResult.value.success) {
        setGoogleDriveStatus(googleDriveStatusResult.value.data);
        
        if (googleDriveStatusResult.value.data.isAuthenticated) {
          try {
            const googleBackupsResult = await enhancedBackupService.listGoogleDriveBackups();
            if (googleBackupsResult.success) {
              setGoogleDriveBackups(googleBackupsResult.backups || []);
            }
          } catch (error) {
            console.error('Failed to load Google Drive backups:', error);
          }
        }
      }

      if (schedulerStatusResult.status === 'fulfilled' && schedulerStatusResult.value.success) {
        const schedulerData = schedulerStatusResult.value.data;
        setSchedulerStatus(schedulerData);
        setSchedulerSettings(prev => ({
          ...prev,
          ...schedulerData.settings
        }));
      }

    } catch (error) {
      console.error('Error loading backup data:', error);
      toast.error('Failed to load backup data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateBackup = async (uploadToGoogleDrive = false) => {
    setCreating(true);
    try {
      toast.loading('Creating enhanced backup...', { id: 'backup-create' });
      
      const result = await enhancedBackupService.createBackup(uploadToGoogleDrive);

      if (result.success) {
        toast.success('Enhanced backup created successfully!', { id: 'backup-create' });
        
        const sizeFormatted = enhancedBackupService.formatBackupSize(result.data.size);
        toast.success(`Backup created: ${result.data.backupId} (${sizeFormatted})`);
        
        if (result.data.googleDrive) {
          toast.success('Backup uploaded to Google Drive successfully!');
        }
        
        loadData();
      } else {
        toast.error(result.message || 'Failed to create backup', { id: 'backup-create' });
      }
    } catch (error) {
      console.error('Backup creation error:', error);
      toast.error('Failed to create backup: ' + error.message, { id: 'backup-create' });
    } finally {
      setCreating(false);
    }
  };

  const handleGoogleDriveAuth = async () => {
    try {
      toast.loading('Getting Google Drive authentication URL...', { id: 'google-auth' });

      // Get the authentication URL from the backend
      const authUrlResult = await enhancedBackupService.getGoogleDriveAuthUrl();

      if (!authUrlResult.success || !authUrlResult.authUrl) {
        toast.error('Failed to get Google Drive authentication URL', { id: 'google-auth' });
        return;
      }

      toast.loading('Opening Google Drive authentication...', { id: 'google-auth' });

      const code = await enhancedBackupService.openGoogleDriveAuthWindow(authUrlResult.authUrl);

      const result = await enhancedBackupService.authenticateGoogleDrive(code);

      if (result.success) {
        toast.success('Google Drive authenticated successfully!', { id: 'google-auth' });
        loadData();
      } else {
        toast.error(result.message || 'Authentication failed', { id: 'google-auth' });
      }
    } catch (error) {
      console.error('Google Drive authentication error:', error);
      toast.error('Authentication failed: ' + error.message, { id: 'google-auth' });
    }
  };

  const handleGoogleDriveDisconnect = async () => {
    // Show confirmation dialog
    if (!window.confirm('Are you sure you want to disconnect from Google Drive?\n\nThis will remove the stored authentication tokens and you will need to reconnect to upload backups to Google Drive.')) {
      return;
    }

    try {
      toast.loading('Disconnecting from Google Drive...', { id: 'google-disconnect' });

      const result = await enhancedBackupService.disconnectGoogleDrive();

      if (result.success) {
        toast.success('Disconnected from Google Drive successfully!', { id: 'google-disconnect' });
        loadData();
      } else {
        toast.error(result.message || 'Failed to disconnect', { id: 'google-disconnect' });
      }
    } catch (error) {
      console.error('Google Drive disconnect error:', error);
      toast.error('Failed to disconnect: ' + error.message, { id: 'google-disconnect' });
    }
  };

  const handleSchedulerToggle = async () => {
    try {
      if (schedulerStatus.isEnabled) {
        await enhancedBackupService.stopScheduler();
        toast.success('Backup scheduler stopped');
      } else {
        await enhancedBackupService.startScheduler();
        toast.success('Backup scheduler started');
      }
      loadData();
    } catch (error) {
      console.error('Scheduler toggle error:', error);
      toast.error('Failed to toggle scheduler: ' + error.message);
    }
  };

  const handleSchedulerSettingsUpdate = async () => {
    try {
      toast.loading('Updating scheduler settings...', { id: 'scheduler-update' });

      const result = await enhancedBackupService.updateSchedulerSettings(schedulerSettings);

      if (result.success) {
        toast.success('Scheduler settings updated successfully!', { id: 'scheduler-update' });
        loadData();
      } else {
        toast.error(result.message || 'Failed to update settings', { id: 'scheduler-update' });
      }
    } catch (error) {
      console.error('Scheduler settings update error:', error);
      toast.error('Failed to update settings: ' + error.message, { id: 'scheduler-update' });
    }
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      try {
        enhancedBackupService.validateBackupFile(file);
        setSelectedFile(file);
        toast.success(`Selected backup file: ${file.name}`);
      } catch (error) {
        toast.error(error.message);
        setSelectedFile(null);
        event.target.value = '';
      }
    }
  };

  const handleRestoreFromFile = async () => {
    if (!selectedFile) {
      toast.error('Please select a backup file first');
      return;
    }

    // Show confirmation dialog
    const confirmed = window.confirm(
      `⚠️ WARNING: This will replace ALL current data with the backup data.\n\n` +
      `File: ${selectedFile.name}\n` +
      `Size: ${formatFileSize(selectedFile.size)}\n\n` +
      `A pre-restore backup will be created automatically.\n\n` +
      `Are you sure you want to continue?`
    );

    if (!confirmed) {
      return;
    }

    setRestoring(true);
    try {
      toast.loading('Restoring database from backup file...', { id: 'restore' });

      const result = await enhancedBackupService.restoreFromFile(selectedFile);

      if (result.success) {
        toast.success('Database restored successfully!', { id: 'restore' });

        // Show restore summary
        const totalRecords = result.data.restoredFrom.totalRecords;
        toast.success(`Restored ${totalRecords} records from backup`);

        // Reset form
        setSelectedFile(null);
        setShowRestoreModal(false);
        document.getElementById('backup-file-input').value = '';

        // Refresh data
        loadData();
      } else {
        toast.error(result.message || 'Failed to restore backup', { id: 'restore' });
      }
    } catch (error) {
      console.error('Restore error:', error);
      toast.error('Failed to restore backup: ' + error.message, { id: 'restore' });
    } finally {
      setRestoring(false);
    }
  };

  const handleCancelRestore = () => {
    setSelectedFile(null);
    setShowRestoreModal(false);
    document.getElementById('backup-file-input').value = '';
  };

  const handleDownloadBackup = async (fileName) => {
    try {
      toast.loading('Downloading backup...', { id: 'download' });
      await enhancedBackupService.downloadBackup(fileName);
      toast.success('Backup downloaded successfully!', { id: 'download' });
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download backup: ' + error.message, { id: 'download' });
    }
  };

  const handleDeleteBackup = async (fileName) => {
    // Show confirmation dialog
    if (!window.confirm(`Are you sure you want to delete the backup "${fileName}"?\n\nThis action cannot be undone.`)) {
      return;
    }

    try {
      toast.loading('Deleting backup...', { id: 'delete' });
      const result = await enhancedBackupService.deleteBackup(fileName);

      if (result.success) {
        toast.success('Backup deleted successfully!', { id: 'delete' });
        // Refresh the backup list
        loadData();
      } else {
        toast.error(result.message || 'Failed to delete backup', { id: 'delete' });
      }
    } catch (error) {
      console.error('Delete error:', error);
      toast.error('Failed to delete backup: ' + error.message, { id: 'delete' });
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (timestamp) => {
    return new Date(timestamp).toLocaleString();
  };

  const tabs = [
    { id: 'backups', name: 'Backups', icon: DocumentArrowDownIcon },
    { id: 'restore', name: 'Restore', icon: CloudArrowUpIcon },
    { id: 'google-drive', name: 'Google Drive', icon: CloudIcon },
    { id: 'scheduler', name: 'Scheduler', icon: ClockIcon }
  ];

  if (loading) {
    return (
      <Layout title="Enhanced Backup Management">
        <div className="flex justify-center items-center h-64">
          <LoadingSpinner />
        </div>
      </Layout>
    );
  }

  return (
    <Layout title="Enhanced Backup Management">
      <div className="space-y-6">
        {/* Header */}
        <div className="bg-white dark:bg-dark-800 shadow rounded-lg p-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                Enhanced Backup Management
              </h1>
              <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                Comprehensive backup system with Google Drive integration and automated scheduling
              </p>
            </div>
            <div className="flex space-x-3">
              <button
                onClick={() => handleCreateBackup(false)}
                disabled={creating}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
              >
                {creating ? (
                  <>
                    <div className="animate-spin -ml-1 mr-3 h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                    Creating...
                  </>
                ) : (
                  <>
                    <DocumentArrowDownIcon className="h-4 w-4 mr-2" />
                    Create Local Backup
                  </>
                )}
              </button>
              
              {googleDriveStatus.isAuthenticated && (
                <button
                  onClick={() => handleCreateBackup(true)}
                  disabled={creating}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                >
                  {creating ? (
                    <>
                      <div className="animate-spin -ml-1 mr-3 h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                      Creating...
                    </>
                  ) : (
                    <>
                      <CloudArrowUpIcon className="h-4 w-4 mr-2" />
                      Create & Upload to Drive
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white dark:bg-dark-800 shadow rounded-lg">
          <div className="border-b border-gray-200 dark:border-gray-700">
            <nav className="-mb-px flex space-x-8 px-6">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`py-4 px-4 border-b-2 font-medium text-sm flex items-center space-x-2 rounded-t-lg ${
                      activeTab === tab.id
                        ? 'bg-orange-500 text-white border-orange-500'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                    }`}
                  >
                    <Icon className="h-4 w-4" />
                    <span>{tab.name}</span>
                  </button>
                );
              })}
            </nav>
          </div>

          {/* Tab Content */}
          <div className="p-6">
            {activeTab === 'backups' && (
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                    Local Backups
                  </h3>
                  <span className="text-sm text-gray-500 dark:text-gray-400">
                    {localBackups.length} backup{localBackups.length !== 1 ? 's' : ''}
                  </span>
                </div>

                {localBackups.length === 0 ? (
                  <div className="text-center py-12">
                    <DocumentArrowDownIcon className="mx-auto h-12 w-12 text-gray-400" />
                    <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">No backups</h3>
                    <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                      Get started by creating your first backup.
                    </p>
                  </div>
                ) : (
                  <div className="bg-white dark:bg-dark-700 shadow overflow-hidden sm:rounded-md">
                    <ul className="divide-y divide-gray-200 dark:divide-gray-600">
                      {localBackups.map((backup, index) => (
                        <li key={index} className="px-6 py-4">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center">
                              <div className="flex-shrink-0">
                                <DocumentArrowDownIcon className="h-6 w-6 text-gray-400" />
                              </div>
                              <div className="ml-4">
                                <div className="text-sm font-medium text-gray-900 dark:text-white">
                                  {backup.fileName}
                                </div>
                                <div className="text-sm text-gray-500 dark:text-gray-400">
                                  {formatDate(backup.createdAt)} • {formatFileSize(backup.size)}
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center space-x-2">
                              <button
                                onClick={() => handleDownloadBackup(backup.fileName)}
                                className="inline-flex items-center px-3 py-1 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 dark:bg-dark-600 dark:text-gray-300 dark:border-gray-500 dark:hover:bg-dark-500"
                              >
                                <CloudArrowDownIcon className="h-4 w-4 mr-1" />
                                Download
                              </button>
                              <button
                                onClick={() => handleDeleteBackup(backup.fileName)}
                                className="inline-flex items-center px-3 py-1 border border-red-300 shadow-sm text-sm leading-4 font-medium rounded-md text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 dark:bg-dark-600 dark:text-red-300 dark:border-red-500 dark:hover:bg-red-900/20"
                                title="Delete backup"
                              >
                                <TrashIcon className="h-4 w-4 mr-1" />
                                Delete
                              </button>
                            </div>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'restore' && (
              <div className="space-y-6">
                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
                  <div className="flex items-center">
                    <ExclamationTriangleIcon className="h-5 w-5 text-red-400 mr-2" />
                    <h3 className="text-sm font-medium text-red-800 dark:text-red-200">
                      Database Restore
                    </h3>
                  </div>
                  <div className="mt-2 text-sm text-red-700 dark:text-red-300">
                    <p className="mb-2">
                      <strong>⚠️ WARNING:</strong> Restoring a backup will replace ALL current data in the database.
                    </p>
                    <ul className="list-disc list-inside space-y-1">
                      <li>A pre-restore backup will be created automatically for safety</li>
                      <li>Only upload backup files created by this system</li>
                      <li>The restore process cannot be undone (except by restoring the pre-restore backup)</li>
                    </ul>
                  </div>
                </div>

                <div className="bg-white dark:bg-dark-700 shadow rounded-lg p-6">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                    Upload Backup File
                  </h3>

                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Select Backup File (.zip)
                      </label>
                      <input
                        id="backup-file-input"
                        type="file"
                        accept=".zip"
                        onChange={handleFileSelect}
                        className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-primary-50 file:text-primary-700 hover:file:bg-primary-100 dark:file:bg-primary-900 dark:file:text-primary-300"
                      />
                      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                        Only ZIP backup files created by this system are supported
                      </p>
                    </div>

                    {selectedFile && (
                      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                        <h4 className="text-sm font-medium text-blue-800 dark:text-blue-200 mb-2">
                          Selected File
                        </h4>
                        <div className="text-sm text-blue-700 dark:text-blue-300">
                          <p><strong>Name:</strong> {selectedFile.name}</p>
                          <p><strong>Size:</strong> {formatFileSize(selectedFile.size)}</p>
                          <p><strong>Last Modified:</strong> {formatDate(selectedFile.lastModified)}</p>
                        </div>
                      </div>
                    )}

                    <div className="flex items-center space-x-3">
                      <button
                        onClick={handleRestoreFromFile}
                        disabled={!selectedFile || restoring}
                        className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {restoring ? (
                          <>
                            <div className="animate-spin -ml-1 mr-3 h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                            Restoring...
                          </>
                        ) : (
                          <>
                            <CloudArrowUpIcon className="h-4 w-4 mr-2" />
                            Restore Database
                          </>
                        )}
                      </button>

                      {selectedFile && (
                        <button
                          onClick={handleCancelRestore}
                          disabled={restoring}
                          className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 dark:bg-dark-600 dark:text-gray-300 dark:border-gray-500 dark:hover:bg-dark-500 disabled:opacity-50"
                        >
                          Cancel
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 dark:bg-dark-700 border border-gray-200 dark:border-gray-600 rounded-lg p-4">
                  <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-2">
                    Restore Process
                  </h4>
                  <ol className="list-decimal list-inside text-sm text-gray-600 dark:text-gray-400 space-y-1">
                    <li>Pre-restore backup is created automatically</li>
                    <li>Current database data is cleared</li>
                    <li>Backup data is imported into the database</li>
                    <li>System is ready with restored data</li>
                  </ol>
                </div>
              </div>
            )}

            {activeTab === 'google-drive' && (
              <div className="space-y-6">
                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                  <div className="flex items-center">
                    <CloudIcon className="h-5 w-5 text-blue-400 mr-2" />
                    <h3 className="text-sm font-medium text-blue-800 dark:text-blue-200">
                      Google Drive Integration
                    </h3>
                  </div>
                  <div className="mt-2 text-sm text-blue-700 dark:text-blue-300">
                    {googleDriveStatus.isAuthenticated ? (
                      <div>
                        <div className="flex items-center mb-3">
                          <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2" />
                          Connected to Google Drive
                        </div>
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={handleGoogleDriveAuth}
                            className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                          >
                            <LinkIcon className="h-4 w-4 mr-2" />
                            Reconnect
                          </button>
                          <button
                            onClick={handleGoogleDriveDisconnect}
                            className="inline-flex items-center px-3 py-2 border border-red-300 shadow-sm text-sm leading-4 font-medium rounded-md text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 dark:bg-dark-600 dark:text-red-300 dark:border-red-500 dark:hover:bg-red-900/20"
                          >
                            <TrashIcon className="h-4 w-4 mr-2" />
                            Disconnect
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div>
                        <div className="flex items-center mb-2">
                          <ExclamationTriangleIcon className="h-4 w-4 text-yellow-500 mr-2" />
                          Not connected to Google Drive
                        </div>
                        <button
                          onClick={handleGoogleDriveAuth}
                          className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                        >
                          <LinkIcon className="h-4 w-4 mr-2" />
                          Connect to Google Drive
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                {googleDriveStatus.isAuthenticated && (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                        Google Drive Backups
                      </h3>
                      <span className="text-sm text-gray-500 dark:text-gray-400">
                        {googleDriveBackups.length} backup{googleDriveBackups.length !== 1 ? 's' : ''}
                      </span>
                    </div>

                    {googleDriveBackups.length === 0 ? (
                      <div className="text-center py-8">
                        <CloudIcon className="mx-auto h-12 w-12 text-gray-400" />
                        <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">No Google Drive backups</h3>
                        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                          Create a backup with Google Drive upload enabled.
                        </p>
                      </div>
                    ) : (
                      <div className="bg-white dark:bg-dark-700 shadow overflow-hidden sm:rounded-md">
                        <ul className="divide-y divide-gray-200 dark:divide-gray-600">
                          {googleDriveBackups.map((backup, index) => (
                            <li key={backup.id} className="px-6 py-4">
                              <div className="flex items-center justify-between">
                                <div className="flex items-center">
                                  <div className="flex-shrink-0">
                                    <CloudIcon className="h-6 w-6 text-blue-500" />
                                  </div>
                                  <div className="ml-4">
                                    <div className="text-sm font-medium text-gray-900 dark:text-white">
                                      {backup.name}
                                    </div>
                                    <div className="text-sm text-gray-500 dark:text-gray-400">
                                      {formatDate(backup.createdTime)} • {formatFileSize(backup.size)}
                                    </div>
                                  </div>
                                </div>
                                <div className="flex items-center space-x-2">
                                  <button
                                    onClick={() => window.open(`https://drive.google.com/file/d/${backup.id}/view`, '_blank')}
                                    className="inline-flex items-center px-3 py-1 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 dark:bg-dark-600 dark:text-gray-300 dark:border-gray-500 dark:hover:bg-dark-500"
                                  >
                                    <CloudArrowDownIcon className="h-4 w-4 mr-1" />
                                    View in Drive
                                  </button>
                                </div>
                              </div>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {activeTab === 'scheduler' && (
              <div className="space-y-6">
                <div className="bg-gray-50 dark:bg-dark-700 border border-gray-200 dark:border-gray-600 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <ClockIcon className="h-5 w-5 text-gray-400 mr-2" />
                      <div>
                        <h3 className="text-sm font-medium text-gray-900 dark:text-white">
                          Backup Scheduler
                        </h3>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          {schedulerStatus.isEnabled ? 'Running' : 'Stopped'} •
                          Next backup: {schedulerStatus.nextBackupTime ? formatDate(schedulerStatus.nextBackupTime) : 'Not scheduled'}
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={handleSchedulerToggle}
                      className={`inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                        schedulerStatus.isEnabled
                          ? 'text-white bg-red-600 hover:bg-red-700 focus:ring-red-500'
                          : 'text-white bg-green-600 hover:bg-green-700 focus:ring-green-500'
                      }`}
                    >
                      {schedulerStatus.isEnabled ? (
                        <>
                          <StopIcon className="h-4 w-4 mr-2" />
                          Stop Scheduler
                        </>
                      ) : (
                        <>
                          <PlayIcon className="h-4 w-4 mr-2" />
                          Start Scheduler
                        </>
                      )}
                    </button>
                  </div>
                </div>

                <div className="bg-white dark:bg-dark-700 shadow rounded-lg p-6">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                    Scheduler Settings
                  </h3>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Backup Frequency
                      </label>
                      <select
                        value={schedulerSettings.frequency}
                        onChange={(e) => setSchedulerSettings(prev => ({ ...prev, frequency: e.target.value }))}
                        className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 dark:bg-dark-600 dark:border-gray-500 dark:text-white"
                      >
                        <option value="daily">Daily</option>
                        <option value="weekly">Weekly</option>
                        <option value="monthly">Monthly</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Backup Time
                      </label>
                      <input
                        type="time"
                        value={schedulerSettings.time}
                        onChange={(e) => setSchedulerSettings(prev => ({ ...prev, time: e.target.value }))}
                        className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 dark:bg-dark-600 dark:border-gray-500 dark:text-white"
                      />
                    </div>

                    <div className="md:col-span-2">
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          id="autoUploadToGoogleDrive"
                          checked={schedulerSettings.autoUploadToGoogleDrive}
                          onChange={(e) => setSchedulerSettings(prev => ({ ...prev, autoUploadToGoogleDrive: e.target.checked }))}
                          className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                        />
                        <label htmlFor="autoUploadToGoogleDrive" className="ml-2 block text-sm text-gray-900 dark:text-white">
                          Automatically upload backups to Google Drive
                        </label>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Max Local Backups
                      </label>
                      <input
                        type="number"
                        min="1"
                        max="100"
                        value={schedulerSettings.maxLocalBackups}
                        onChange={(e) => setSchedulerSettings(prev => ({ ...prev, maxLocalBackups: parseInt(e.target.value) }))}
                        className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 dark:bg-dark-600 dark:border-gray-500 dark:text-white"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Max Google Drive Backups
                      </label>
                      <input
                        type="number"
                        min="1"
                        max="1000"
                        value={schedulerSettings.maxGoogleDriveBackups}
                        onChange={(e) => setSchedulerSettings(prev => ({ ...prev, maxGoogleDriveBackups: parseInt(e.target.value) }))}
                        className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 dark:bg-dark-600 dark:border-gray-500 dark:text-white"
                      />
                    </div>
                  </div>

                  <div className="mt-6">
                    <button
                      onClick={handleSchedulerSettingsUpdate}
                      className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                    >
                      <Cog6ToothIcon className="h-4 w-4 mr-2" />
                      Update Settings
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default EnhancedBackupManagement;
